import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import {
  Container,
  Typography,
  Box,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
} from '@mui/material';
import {
  ArrowBack,
  Delete,
  AutoStories,
} from '@mui/icons-material';
import axios from 'axios';
import StoryScene from './StoryScene';

function InteractiveStory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingChoice, setProcessingChoice] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchStory();
  }, [id]);

  const fetchStory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/stories/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStory(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching story:', error);
      setLoading(false);
      alert('Failed to load story. Please try again.');
      navigate('/stories');
    }
  };

  const handleChoiceSelect = async (choice) => {
    setProcessingChoice(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/stories/${id}/choice`,
        { choice },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setStory(response.data);
    } catch (error) {
      console.error('Error processing choice:', error);
      alert('Failed to process choice. Please try again.');
    } finally {
      setProcessingChoice(false);
      // Scroll to top to see the new scene
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDeleteStory = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/stories/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate('/stories');
    } catch (error) {
      console.error('Error deleting story:', error);
      alert('Failed to delete story. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: '#FAF3E0',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: '#8D7B68', mb: 3 }} />
          <Typography variant="h6" sx={{ color: '#8D7B68', fontWeight: 600 }}>
            Opening the tale...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!story) {
    return null;
  }

  const currentScene = story.scenes[story.scenes.length - 1];

  return (
    <Box sx={{ backgroundColor: '#FAF3E0', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate('/stories')}
              sx={{
                color: '#8D7B68',
                '&:hover': { backgroundColor: 'rgba(141, 123, 104, 0.1)' },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#333',
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                }}
              >
                {story.title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#8D7B68', fontWeight: 600 }}>
                {story.genre} Tale
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setDeleteDialogOpen(true)}
            sx={{
              color: '#d32f2f',
              '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' },
            }}
          >
            <Delete />
          </IconButton>
        </Box>

        {/* Progress Bar */}
        <Box
          sx={{
            mb: 4,
            p: 2,
            backgroundColor: '#F7EEDC',
            border: '2px solid #8D7B68',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" sx={{ color: '#8D7B68', fontWeight: 600 }}>
              Tale Progress
            </Typography>
            <Typography variant="body2" sx={{ color: '#8D7B68', fontWeight: 600 }}>
              {story.currentScene} / ~22 chapters
            </Typography>
          </Box>
          <Box
            sx={{
              width: '100%',
              height: 10,
              backgroundColor: '#DDD',
              borderRadius: 5,
              overflow: 'hidden',
              border: '1px solid #8D7B68',
            }}
          >
            <Box
              sx={{
                width: `${(story.currentScene / 22) * 100}%`,
                height: '100%',
                backgroundColor: '#8D7B68',
                transition: 'width 0.5s ease',
              }}
            />
          </Box>
          {story.isCompleted && (
            <Typography
              variant="body2"
              sx={{
                color: '#4CAF50',
                fontWeight: 600,
                textAlign: 'center',
                mt: 1,
              }}
            >
              ✨ Tale Complete! ✨
            </Typography>
          )}
        </Box>

        {/* Loading State */}
        {processingChoice && (
          <Fade in>
            <Box sx={{ textAlign: 'center', my: 6 }}>
              <CircularProgress size={50} sx={{ color: '#8D7B68', mb: 2 }} />
              <Typography variant="body1" sx={{ color: '#8D7B68', fontWeight: 600 }}>
                The Royal Scribe prepares the next chapter...
              </Typography>
              <Typography variant="body2" sx={{ color: '#555', mt: 1 }}>
                Weaving your choice into the tapestry of fate
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Current Scene */}
        {!processingChoice && (
          <StoryScene
            scene={currentScene}
            onChoiceSelect={handleChoiceSelect}
            isLoading={processingChoice}
            sceneNumber={story.currentScene}
            totalScenes={22}
          />
        )}

        {/* Back to Stories Button (if story is complete) */}
        {story.isCompleted && (
          <Fade in timeout={1200}>
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="contained"
                startIcon={<AutoStories />}
                onClick={() => navigate('/stories')}
                sx={{
                  backgroundColor: '#8D7B68',
                  color: '#FFF',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#7A6859',
                    transform: 'scale(1.03)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Return to Royal Tales
              </Button>
            </Box>
          </Fade>
        )}
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#F4ECE3',
            border: '2px solid #8D7B68',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ color: '#333', fontWeight: 700 }}>
          Delete This Tale?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: '#555' }}>
            Are you certain you wish to delete "{story.title}"? This action cannot be undone, and the tale shall be lost forever.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              color: '#8D7B68',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteStory}
            variant="contained"
            sx={{
              backgroundColor: '#d32f2f',
              color: '#FFF',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#b71c1c',
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default InteractiveStory;

